import React, { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { connect } from "trim-redux";
import { readAdminForegroundOrderNotificationsEnabled } from "../../../config/adminOrderNotificationsPref";
import { isDesktopApp } from "../../../config/desktopApp";
import {
  playFcmNotificationSound,
  resolveNotificationSoundType,
  restoreFcmAudioUnlockFromSession,
  tryUnlockFcmAudio,
  requestBackgroundNotificationWithSound,
} from "../../../config/fcmNotificationSound";

const DEDUP_WINDOW_MS = 10 * 1000;
const recentForegroundNotificationKeys = new Map();

function resolvePayloadTitle(payload) {
  const n = payload?.notification || {};
  const data = payload?.data || {};
  return String(n.title || data.title || "").trim();
}

function resolvePayloadBody(payload) {
  const n = payload?.notification || {};
  const data = payload?.data || {};
  return String(n.body || data.body || "").trim();
}

function resolvePayloadLink(payload) {
  const data = payload?.data || {};
  return (
    payload?.fcmOptions?.link ||
    data.link ||
    data.url ||
    payload?.webpush?.fcm_options?.link ||
    ""
  );
}

function resolveNotificationClickTarget(rawLink) {
  const s = String(rawLink || "").trim();
  if (!s) {
    return null;
  }
  if (s.startsWith("http://") || s.startsWith("https://")) {
    try {
      const url = new URL(s);
      if (
        typeof window !== "undefined" &&
        url.origin === window.location.origin
      ) {
        return {
          kind: "internal",
          path: `${url.pathname}${url.search}${url.hash}`,
        };
      }
      return { kind: "external", url: s };
    } catch {
      return null;
    }
  }
  return { kind: "internal", path: s.startsWith("/") ? s : `/${s}` };
}

function isOrderLikeForegroundFcmPayload(payload) {
  return resolveNotificationSoundType(payload) === "order";
}

function buildForegroundNotificationDedupKey(payload) {
  const messageId = String(payload?.messageId || "").trim();
  if (messageId) {
    return `mid:${messageId}`;
  }
  return `t:${resolvePayloadTitle(payload)}|b:${resolvePayloadBody(payload)}`;
}

function shouldSkipDuplicateForegroundNotification(payload) {
  const key = buildForegroundNotificationDedupKey(payload);
  const now = Date.now();
  const lastSeen = recentForegroundNotificationKeys.get(key);
  if (lastSeen != null && now - lastSeen < DEDUP_WINDOW_MS) {
    return true;
  }
  recentForegroundNotificationKeys.set(key, now);
  for (const [storedKey, seenAt] of recentForegroundNotificationKeys) {
    if (now - seenAt > DEDUP_WINDOW_MS) {
      recentForegroundNotificationKeys.delete(storedKey);
    }
  }
  return false;
}

function isPageActiveForInAppNotifier() {
  if (typeof document === "undefined") {
    return false;
  }
  return document.visibilityState === "visible" && document.hasFocus();
}

function FcmForegroundNotifier({ user }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    restoreFcmAudioUnlockFromSession();

    const onUserGesture = () => {
      tryUnlockFcmAudio();
    };
    document.addEventListener("pointerdown", onUserGesture, true);
    document.addEventListener("click", onUserGesture, true);
    document.addEventListener("keydown", onUserGesture, true);
    document.addEventListener("touchstart", onUserGesture, true);
    return () => {
      document.removeEventListener("pointerdown", onUserGesture, true);
      document.removeEventListener("click", onUserGesture, true);
      document.removeEventListener("keydown", onUserGesture, true);
      document.removeEventListener("touchstart", onUserGesture, true);
    };
  }, []);

  useEffect(() => {
    const handler = (event) => {
      try {
        const payload = event.detail;
        if (!payload) {
          return;
        }
        if (shouldSkipDuplicateForegroundNotification(payload)) {
          return;
        }

        if (isDesktopApp() && isPageActiveForInAppNotifier()) {
          return;
        }

        if (!isPageActiveForInAppNotifier()) {
          if (!isDesktopApp()) {
            requestBackgroundNotificationWithSound(payload);
          }
          return;
        }

        const rawLink = resolvePayloadLink(payload);
        const clickTarget = resolveNotificationClickTarget(rawLink);

        const isAdmin = user?.role?.name === "admin";
        if (
          isAdmin &&
          isOrderLikeForegroundFcmPayload(payload) &&
          !readAdminForegroundOrderNotificationsEnabled()
        ) {
          return;
        }

        const title = resolvePayloadTitle(payload) || "Kolay Doy";
        const body = resolvePayloadBody(payload);
        const key = `fcm-${payload.messageId || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`}`;

        const onClick = () => {
          tryUnlockFcmAudio();
          if (clickTarget?.kind === "internal") {
            navigate(clickTarget.path);
          } else if (clickTarget?.kind === "external") {
            window.location.href = clickTarget.url;
          }
          closeSnackbar(key);
        };

        enqueueSnackbar(
          <Box
            role="button"
            onClick={onClick}
            sx={{ cursor: "pointer", maxWidth: 360 }}
          >
            <Typography component="div" variant="subtitle2" fontWeight={700}>
              {title}
            </Typography>
            {body ?
              <Typography
                component="div"
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {body}
              </Typography>
            : null}
          </Box>,
          {
            key,
            variant: "info",
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            autoHideDuration: 9000,
          },
        );

        playFcmNotificationSound(payload);
      } catch (e) {
        console.error("FcmForegroundNotifier handler failed", e);
      }
    };

    window.addEventListener("fcm-foreground-message", handler);
    return () => window.removeEventListener("fcm-foreground-message", handler);
  }, [enqueueSnackbar, closeSnackbar, navigate, user]);

  return null;
}

const mstp = (state) => ({ user: state.user });

export default connect(mstp)(FcmForegroundNotifier);
